import React, { useState } from 'react';
import { formatDateCa } from '../legal-board/legalBoardUi';
import { Disclosure } from '../legal-board/LegalBoardPrimitives';
import { downloadReportPdf, downloadSignedReport, readSignedPdf, uploadSignedReport } from './api';
import { OfficialLink } from './PublicUtilityForms';
import { SIGNING_LINKS } from './constants';

const signerLabel = (signer) => `${signer.name}${signer.role ? ` (${signer.role})` : ''}`;

// What would stop the Generalitat from taking the uploaded copy
function signingProblems(signing) {
    if (signing.outdated) return ['Heu canviat la memòria després de pujar-la signada. Descarregueu-la de nou i torneu-la a signar.'];
    const problems = [];
    if (signing.signatures.length === 0) problems.push('Aquest PDF no porta cap signatura digital.');
    if (signing.broken) problems.push('Una signatura no quadra amb el document: el PDF s’ha modificat després de signar-lo. Torneu a començar des de la memòria descarregada.');
    if (signing.selfSigned) problems.push('Una signatura s’ha fet sense certificat digital, amb una identitat pròpia com la d’Adobe. No serveix: torneu-la a fer amb un certificat.');
    if (signing.others.length > 0) problems.push(`${signing.others.length === 1 ? 'Hi ha una signatura que no és' : 'Hi ha signatures que no són'} de la junta de Registre: ${signing.others.join(', ')}.`);
    return problems;
}

/**
 * The memòria signed by the whole board: step by step with AutoFirma, and a
 * place to upload it as it gets signed so Aleta says who is still missing.
 */
function PublicUtilitySigning({ userId, year, report, canManage, busy, run, onSigning }) {
    const [downloaded, setDownloaded] = useState(false);
    const [openStep, setOpenStep] = useState(null);
    const [paperOpen, setPaperOpen] = useState(false);
    const signing = report.signing || null;
    const signers = report.signers || [];
    const problems = signing ? signingProblems(signing) : [];
    const allSigned = Boolean(signing) && signers.length > 0 && signing.missing === 0 && problems.length === 0;

    const upload = (event) => {
        const [file] = Array.from(event.target.files || []);
        event.target.value = '';
        if (!file) return;
        run(async () => {
            const payload = await uploadSignedReport({ userId, year, file: await readSignedPdf(file) });
            onSigning(payload.signing);
            setOpenStep(null);
        });
    };

    const steps = [
        {
            title: 'Descarrega la memòria en PDF',
            ok: downloaded || Boolean(signing),
            hint: 'És la que signareu. No la modifiquis ni la desis de nou amb un altre programa: es trencarien les signatures.',
            action: (
                <button type="button" className="legal-board-button secondary" disabled={busy} onClick={() => run(async () => { await downloadReportPdf({ userId, year }); setDownloaded(true); })}>
                    <i className="fas fa-download" aria-hidden="true"></i> Descarregar la memòria
                </button>
            ),
        },
        {
            title: 'Cada persona de la junta necessita un certificat digital',
            ok: Boolean(signing?.signatures.length),
            hint: 'Serveix l’idCAT Certificat (és gratuït i es demana per videotrucada), el certificat de la FNMT o el DNI electrònic. L’idCAT Mòbil no serveix per signar documents.',
            action: <OfficialLink href={SIGNING_LINKS.idcat}>Demanar l’idCAT Certificat</OfficialLink>,
        },
        {
            title: 'Signeu-la amb AutoFirma, d’un en un',
            ok: Boolean(signing) && signing.missing === 0,
            hint: (
                <>
                    A AutoFirma, tria «Firmar», obre el PDF i marca «Hacer la firma visible» per posar la signatura al teu requadre.
                    {' '}AutoFirma en desa una còpia acabada en «_signed»: aquesta és la que ha de signar la persona següent.
                    {signers.length > 0 ? <><br />Han de signar: {signers.map(signerLabel).join(', ')}.</> : null}
                </>
            ),
            action: <OfficialLink href={SIGNING_LINKS.autofirma}>Descarregar AutoFirma</OfficialLink>,
        },
        {
            title: 'Puja-la aquí',
            ok: allSigned,
            hint: 'Pots pujar-la després de cada signatura: Aleta et dirà qui falta.',
            action: canManage ? (
                <span>
                    <input id={`public-utility-signed-${year}`} className="legal-board-sr-only" type="file" accept=".pdf,application/pdf" onChange={upload} disabled={busy} />
                    <label htmlFor={`public-utility-signed-${year}`} className="legal-board-button secondary" aria-disabled={busy}>
                        <i className="fas fa-upload" aria-hidden="true"></i> {signing ? 'Pujar la versió nova' : 'Pujar la memòria signada'}
                    </label>
                </span>
            ) : null,
        },
    ];
    // Only the step to do is open: the first one not done
    const firstPending = steps.findIndex((step) => !step.ok);
    const currentStep = openStep === null ? firstPending : openStep;

    return (
        <>
            <p className="legal-board-muted">
                La Generalitat la demana signada per la junta. Amb certificat digital es fa des de l’ordinador, sense imprimir res.
            </p>
            <ol className="legal-board-flow public-utility-steps" aria-label="Passos per signar">
                {steps.map((step, index) => {
                    const open = index === currentStep;
                    const state = step.ok ? 'done' : open ? 'active' : 'pending';
                    return (
                        <li key={step.title} className={`legal-board-flow-item ${state}`}>
                            <button type="button" className="legal-board-flow-head" aria-expanded={open} aria-controls={`public-utility-sign-step-${index}`} onClick={() => setOpenStep(open ? -1 : index)}>
                                <span className={`legal-board-flow-icon ${state}`} aria-hidden="true">{step.ok ? <i className="fas fa-check"></i> : index + 1}</span>
                                <span className="legal-board-flow-title">{step.ok ? <span className="legal-board-sr-only">Fet. </span> : null}{step.title}</span>
                                <span className="legal-board-flow-summary"></span>
                                <i className={`fas fa-chevron-${open ? 'up' : 'down'} legal-board-flow-chevron`} aria-hidden="true"></i>
                            </button>
                            {open ? (
                                <div id={`public-utility-sign-step-${index}`} className="legal-board-flow-body">
                                    <p className="public-utility-step-hint">{step.hint}</p>
                                    {step.action ? <div className="public-utility-step-action">{step.action}</div> : null}
                                </div>
                            ) : null}
                        </li>
                    );
                })}
            </ol>

            {signing ? (
                <>
                    <h3 className="public-utility-subtitle">{allSigned ? 'Signada per tota la junta' : signing.missing > 0 && !signing.outdated ? `${signing.missing === 1 ? 'Falta 1 persona' : `Falten ${signing.missing} persones`} per signar` : 'Qui ha signat'}</h3>
                    <ul className="public-utility-checks" aria-label="Qui ha signat">
                        {signing.members.map((member) => (
                            <li key={member.name} className={`public-utility-check ${member.signed ? 'ok' : 'unknown'}`}>
                                <i className={`fas ${member.signed ? 'fa-check-circle' : 'fa-circle'}`} aria-hidden="true"></i>
                                <div className="public-utility-check-body">
                                    <span>
                                        <span className="legal-board-sr-only">{member.signed ? 'Ha signat. ' : 'Encara no ha signat. '}</span>
                                        {signerLabel(member)}
                                    </span>
                                    {member.signed && member.signedOn ? <span className="legal-board-muted legal-board-small">Signat el {formatDateCa(member.signedOn)}</span> : null}
                                </div>
                            </li>
                        ))}
                    </ul>
                    {problems.map((problem) => <p key={problem} className="public-utility-incomplete" role="status">{problem}</p>)}
                    <p className="legal-board-small">
                        Guardada a Aleta: {signing.name} ·{' '}
                        <button type="button" className="legal-board-inline-action" disabled={busy} onClick={() => run(() => downloadSignedReport({ userId, year }))}>descarregar</button>
                    </p>
                    {allSigned ? (
                        <p className="legal-board-muted legal-board-small">
                            Aleta comprova que les signatures quadren amb el document. Si voleu comprovar també que els certificats són vàlids:{' '}
                            <OfficialLink href={SIGNING_LINKS.valide}>VALIDe</OfficialLink>
                        </p>
                    ) : null}
                </>
            ) : null}

            <Disclosure id={`public-utility-paper-${year}`} summary="Sense certificat digital" open={paperOpen} onToggle={() => setPaperOpen((value) => !value)}>
                <p>Imprimiu-la, signeu-la a mà cadascú al seu requadre i escanegeu-la en un sol PDF de menys de 5 MB. També la podeu pujar aquí per tenir-la guardada.</p>
                <p className="legal-board-muted legal-board-small">Presentada per internet, la Generalitat pot demanar-la signada digitalment. Amb certificat no hi ha dubte.</p>
            </Disclosure>
        </>
    );
}

export default PublicUtilitySigning;
